import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';
import * as Icons from 'react-icons/ai';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
    Box, Card, Typography, Stack, TextField, Button, Checkbox, FormControlLabel,
    Link, Divider, IconButton, InputAdornment,
} from '@mui/material';
import ThemeConfig from '../../minimal/theme';
import { clearState, togglePassword, userLogin } from '../../reducers/LoginReducer';
import { usePageTitle } from '../../components';
import logo from '../../assets/logo.png';

const OwnerLogin = () => {
    const dispatch = useDispatch();
    const navigation = useNavigate();
    const loginState = useSelector((state) => state.login);
    const { t } = useTranslation();
    usePageTitle('Login');

    useEffect(() => {
        // Surfaced when the API auto-logged-out an expired session (Api.jsx 401 → ?expired=1)
        if (typeof window !== 'undefined' && window.location.search.includes('expired=1')) {
            toast('Your session expired — please log in again.', { icon: '🔒' });
            window.history.replaceState({}, '', '/login');
        }
        return () => { dispatch(clearState()); };
    }, [dispatch]);

    useEffect(() => {
        if (loginState.isError) {
            toast.error(loginState.errorMessage);
            setTimeout(() => { dispatch(clearState()); }, 5000);
        }
        if (loginState.isSuccess) {
            dispatch(clearState());
            // Full reload so AppReducer re-initializes authUser from the freshly-written localStorage.
            window.location.replace('/owner/dashboard');
        }
    }, [dispatch, loginState, navigation]);

    const inputPassType = loginState.toggle_password ? 'text' : 'password';
    const passwordToggle = (toggle) => dispatch(togglePassword(toggle));

    const LoginSchema = Yup.object().shape({
        email: Yup.string().required('Email is required').email('Invalid Email'),
        password: Yup.string()
            .required('Password is required')
            .min(6, 'Password is too short - should be 6 chars minimum')
            .max(16, 'Password is too long - should be 16 chars minimum'),
    });

    const handleSubmit = (values) => dispatch(userLogin(values));

    return (
        <ThemeConfig>
            <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default', p: 2 }}>
                <Card sx={{ width: '100%', maxWidth: 440, p: { xs: 3, sm: 5 }, boxShadow: (theme) => theme.customShadows.z24 }}>
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <img src={logo} alt="CometQR" style={{ height: 56, width: 'auto' }} />
                        <Typography variant="h4" sx={{ mt: 2 }}>Sign in to CometQR</Typography>
                        <Typography sx={{ color: 'text.secondary', mt: 0.5 }}>Manage your menu, orders and floor.</Typography>
                    </Box>

                    <Formik initialValues={{ email: '', password: '' }} validationSchema={LoginSchema} onSubmit={(values) => handleSubmit(values)}>
                        {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isValid, dirty }) => (
                            <form onSubmit={handleSubmit} autoComplete="off">
                                <Stack spacing={2.5}>
                                    <TextField
                                        fullWidth name="email" type="email" label="Email address"
                                        value={values.email} onChange={handleChange} onBlur={handleBlur}
                                        error={touched.email && Boolean(errors.email)} helperText={touched.email && errors.email}
                                        autoComplete="username"
                                    />
                                    <TextField
                                        fullWidth name="password" type={inputPassType} label="Password"
                                        value={values.password} onChange={handleChange} onBlur={handleBlur}
                                        error={touched.password && Boolean(errors.password)} helperText={touched.password && errors.password}
                                        autoComplete="current-password"
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => passwordToggle(!loginState.toggle_password)} edge="end">
                                                        {loginState.toggle_password ? <Icons.AiOutlineEye /> : <Icons.AiOutlineEyeInvisible />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <FormControlLabel control={<Checkbox name="remember_me" />} label="Remember me" />
                                        <Link href="/forgot-password" variant="body2" underline="hover">Forgot password?</Link>
                                    </Box>
                                    {loginState.isError ? (
                                        <Typography color="error" variant="body2" align="center">{loginState.errorMessage}</Typography>
                                    ) : null}
                                    <Button type="submit" fullWidth size="large" variant="contained" disabled={!(dirty && isValid) || loginState.isFetching}>
                                        {loginState.isFetching ? 'Signing in…' : 'Login'}
                                    </Button>
                                </Stack>
                            </form>
                        )}
                    </Formik>

                    <Typography variant="body2" align="center" sx={{ mt: 3 }}>
                        Don&apos;t have an account? <Link href="/register" underline="hover">Sign up</Link>
                        {'  ·  '}
                        <Link href="/pricing" underline="hover">Plans &amp; pricing</Link>
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="body2" align="center">
                        <Link href="/staff-login" underline="hover" sx={{ color: 'text.secondary' }}>
                            {t('staff_login_owner_to_staff', 'Staff sign-in')}
                        </Link>
                    </Typography>
                </Card>
            </Box>
        </ThemeConfig>
    );
};

export default OwnerLogin;
